import sanity from "/lib/sanity/client";

import Seo from "utils/seo";

import { Section, Block } from "components/Layout";
import CmsBlock from "components/CmsBlock";
import HeaderMedia from "components/HeaderMedia";
import Footer from "components/Footer";
import PortableText from "/components/cms/portableText";
import { useEffect } from "react";
import { BikeATStore, IBikeStore, getBikeModelAndSubscriptions } from "/lib/bike";
import { ISubscriptionStore, SubscriptionATStore } from "/lib/subscription";
import { BikeModel, ImageObject } from "/lib/sanity";
import { PortableTextBlock } from "@portabletext/types";

type ArticlePageProps = {
  page: QueryData;
  slug: string;
  bikeModel: BikeItemContent;
};

const ArticlePage = ({ page, slug, bikeModel }: ArticlePageProps) => {
  const { title, intro, content = [], headerMedia = [] } = page || {};

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo isFrontpage={false} page={page} />

      <article>
        <Section limitedWidthSmall outer="firstSection">
          <Block bottom={7} mobileTop={4}>
            {title && <h1>{title}</h1>}
          </Block>
          {intro && <PortableText blocks={intro.richText} />}
        </Section>

        {!!headerMedia?.length && <HeaderMedia data={headerMedia} />}

        {content?.map((item) => {
          // const blockData = { ...item, bikeModelId: page.bikeModelId };
          const blockData = {
            ...item,
            bikeModelData: bikeModel
          };
          return <CmsBlock data={blockData} key={item._key} />;
        })}
      </article>

      <Footer slug={slug} />
    </>
  );
};

// Find all pages - needed to build everything static
const queryAllPages = `*[_type == "articles" && slug.current != ''] 
{'slug': slug.current}`;

// Get data for this particular page based on slug
const query = `*[_type == "articles" && slug.current == $slug]{
	title,
  intro,
  content[]{
    ...,
    "faq": title, faq[]->{_id, title, richText, "category": category[]->{title}},
    "image": image{..., asset->{_id, metadata{dimensions, lqip}}},   
    "bikeModel": bikeModel[]->{ id, title, text, styledList,price, linkItems, products[]->{title, price, category[]->{title}}, "image": image{..., asset->{_id, metadata{dimensions, lqip}}}},
     "articles": articles[]->{ title, intro ,slug, content[]{...} },
  },
  headerMedia,
  seo,
  "bikeModelId": content[_type == "bikeModel"]{
    bikeModel[]->{
      id
    }
   }[0]["bikeModel"][0].id
}[0]`;

export const getStaticPaths = async () => {
  const pages = (await sanity.fetch(queryAllPages)) || [];
  const paths = pages.map((page: { slug: string }) => ({
    params: { slug: page.slug }
  }));
  return { paths, fallback: false };
};

// The type here is not fully typed up. Because I am lazy.
type QueryData = {
  title: string;
  intro: {
    richText: PortableTextBlock[];
  };
  headerMedia?: Parameters<typeof HeaderMedia>[0]["data"];
  bikeModelId?: string;
  content: {
    _key: string;
    _type: string;
    reverseFirst: boolean;
    alternating: boolean;
    bikeModel: BikeModel[] | null;
  }[];
};

type ContentWithBikeModel = Omit<QueryData["content"][0], "bikeModel"> & {
  bikeModel: BikeModel[];
};

type BikeItemContent = {
  cms: Omit<ContentWithBikeModel, "bikeModel"> & { bikeModel: BikeModel };
  bike: Awaited<ReturnType<typeof getBikeModelAndSubscriptions>>["bikeModel"];
  bikeSubscriptions: Awaited<ReturnType<typeof getBikeModelAndSubscriptions>>["subscriptions"];
};

export const getStaticProps = async ({ params }: { params: { slug: string } }) => {
  const { slug } = params;
  const page: QueryData = await sanity.fetch(query, { slug });

  const bikeStore: IBikeStore = new BikeATStore();
  const subscriptionStore: ISubscriptionStore = new SubscriptionATStore();

  const bikeModelId = page.bikeModelId;

  if (!bikeModelId) {
    return {
      props: {
        page,
        slug
      }
    };
  }

  const { bikeModel, subscriptions } = await getBikeModelAndSubscriptions(
    bikeStore,
    subscriptionStore,
    bikeModelId
  );

  const bikeModelContent = page.content.find(
    (c): c is ContentWithBikeModel => c.bikeModel !== null
  );

  if (!bikeModelContent) {
    throw new Error("No Sanity page content for bike model");
  }

  // Picks the first bike in the list.
  // This could potentially cause some confusion if someone for some reason configures an article with multiple bikes.
  const massagedContent = { ...bikeModelContent, bikeModel: bikeModelContent.bikeModel[0] };

  const supplementedBikeModelData: BikeItemContent = {
    cms: massagedContent,
    bike: bikeModel,
    bikeSubscriptions: subscriptions
  };

  return {
    props: {
      page,
      slug,
      bikeModel: supplementedBikeModelData
    }
  };
};

export default ArticlePage;
